import { createSelector } from '@reduxjs/toolkit';
import { type FeatureSelectorWithFallback } from '@store/storeTypes';
import { type RootState } from '@store/types';

import { userApi } from './api';
import { type UserState } from './slice';
import { type SubjectLanguage } from './types';

const emptyState: UserState = {
  authStatus: 'initializing',
  loading: false,
  awaitingLogin: false,
  unauthorized: false,
  isAuthenticated: false,
  newFeature: false,
  availableSpecialties: undefined,
  auth0Picture: undefined,
};

// Base selector for slice state
export const selectUserState: FeatureSelectorWithFallback<UserState, typeof emptyState> = (
  state: RootState,
) => state.user || emptyState;

// Auth state selectors (from slice)
export const selectAuthStatus = createSelector([selectUserState], state => state.authStatus);

export const selectLoading = createSelector([selectUserState], state => state.loading);

export const selectAwaitingLogin = createSelector([selectUserState], state => state.awaitingLogin);

export const selectUnauthorized = createSelector([selectUserState], state => state.unauthorized);

export const selectNewFeature = createSelector([selectUserState], state => state.newFeature);

export const selectIsAuthenticatedFromState = createSelector(
  [selectUserState],
  state => state.isAuthenticated,
);

export const selectAvailableSpecialties = createSelector(
  [selectUserState],
  state => state.availableSpecialties || [],
);

export const selectAuth0Picture = createSelector([selectUserState], state => state.auth0Picture);

// Constants for empty values to ensure referential equality
const EMPTY_ARRAY: unknown[] = [];
const EMPTY_OBJECT = {};
const EMPTY_STRING = '';

// All server state selectors return empty/default values
// The actual data comes from RTK Query hooks in components
// These are here for backward compatibility only

export const selectUserId = createSelector([(state: RootState) => state], () => undefined);

export const selectUserInfo = createSelector([(state: RootState) => state], () => undefined);

export const selectEmailVerified = createSelector([(state: RootState) => state], () => undefined);

export const selectPaymentStatus = createSelector([(state: RootState) => state], () => undefined);

export const selectLockedOut = createSelector([(state: RootState) => state], () => false);

export const selectFeatureFlags = createSelector(
  [(state: RootState) => state],
  () => EMPTY_OBJECT as Record<string, boolean>,
);

export const selectFeatureFlag = (_flagName: string) =>
  createSelector([(state: RootState) => state], () => false);

export const selectGroupUserRoles = createSelector(
  [(state: RootState) => state],
  () => EMPTY_ARRAY,
);

export const selectGroupIds = createSelector([(state: RootState) => state], () => EMPTY_ARRAY);

export const selectOnboardingSteps = createSelector(
  [(state: RootState) => state],
  () => EMPTY_OBJECT,
);

export const selectOnboardingStep = (_stepName: string) =>
  createSelector([(state: RootState) => state], () => false);

export const selectTutorialProgress = createSelector(
  [(state: RootState) => state],
  () => EMPTY_OBJECT,
);

export const selectTutorialById = (_tutorialId: string) =>
  createSelector([(state: RootState) => state], () => undefined);

export const selectTemplatesList = createSelector([(state: RootState) => state], () => EMPTY_ARRAY);

export const selectTroubleWords = createSelector([(state: RootState) => state], () => EMPTY_ARRAY);

const DEFAULT_SUBJECT_LANGUAGE: SubjectLanguage = 'patient';

// Helper functions for text transformations
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const pluralize = (s: string) => (s.endsWith('s') ? s : `${s}s`);

// Base selector for languages and preferences from RTK Query cache
export const selectLanguagesAndPreferences = createSelector(
  userApi.endpoints.getLanguagesAndPreferences.select(undefined),
  res => res.data,
);

// Selector that checks if languages and preferences query has an error
export const selectLanguagesAndPreferencesError = createSelector(
  userApi.endpoints.getLanguagesAndPreferences.select(undefined),
  res => res.error,
);

const selectUserInfoRead = userApi.endpoints.getUserInfo.select(undefined);

/** The signed-in person's display name from the user-info read; null before it lands. */
export const selectSignedInName = (state: RootState): string | null =>
  /* One `api` slice serves every injected endpoint, but RTK types each
     injection on its own, so the store's shape is cast to the one this
     endpoint's selector expects. */
  selectUserInfoRead(state as unknown as Parameters<typeof selectUserInfoRead>[0]).data?.user_info
    .name || null;

// Raw selector that uses RTK Query cache to get subject language preference
export const selectSubjectLanguage = createSelector(
  selectLanguagesAndPreferences,
  data => data?.user_preferences?.subject ?? DEFAULT_SUBJECT_LANGUAGE,
);

// Derived selector that returns four pre-computed variants of the subject language
export const selectSubjectLanguageVariants = createSelector(selectSubjectLanguage, label => {
  const pluralLabel = pluralize(label);

  return {
    /** singular lower-case (raw)      */ singular: label, // "patient"
    /** singular Capitalized          */ singularCap: capitalize(label), // "Patient"
    /** plural lower-case             */ plural: pluralLabel, // "patients"
    /** plural Capitalized            */ pluralCap: capitalize(pluralLabel), // "Patients"
  };
});

export const selectDefaultTimezone = createSelector([(state: RootState) => state], () => undefined);

export const selectGamification = createSelector([(state: RootState) => state], () => undefined);

export const selectReferralInfo = createSelector([(state: RootState) => state], () => undefined);

export const selectUserMetrics = createSelector([(state: RootState) => state], () => undefined);

export const selectProviderBillingInfo = createSelector(
  [(state: RootState) => state],
  () => undefined,
);

export const selectPatientMatchEnrolled = createSelector(
  [(state: RootState) => state],
  () => false,
);

export const selectClearinghouseLinked = createSelector(
  [(state: RootState) => state],
  () => undefined,
);

// Composite selectors
export const selectIsAuthenticated = createSelector([(state: RootState) => state], () => false);

export const selectUserName = createSelector([(state: RootState) => state], () => EMPTY_STRING);

export const selectUserEmail = createSelector([(state: RootState) => state], () => EMPTY_STRING);

export const selectUserRoles = createSelector([(state: RootState) => state], () => EMPTY_ARRAY);

// For backward compatibility - returns null since data comes from hooks
export const selectFullUserState = createSelector([(state: RootState) => state], () => null);

// This is not needed with RTK Query
export const selectUserDataLoaded = createSelector([(state: RootState) => state], () => false);
